// src/components/sections/AcquisitionCosts.jsx
import React from "react";
import { Briefcase } from "lucide-react";
import FormattedNumberInput from "../FormattedNumberInput";
import FieldHelpLabel from "../FieldHelpLabel";
import { useLanguage } from "../../contexts/LanguageContext";

export default function AcquisitionCosts({
  costs = {},
  onChange,
  lockedFields = {},
  advancedExpenses,
  analysis = {},
  financingType,
}) {
  const { t } = useLanguage();
  const handleChange = (field, value) => {
    onChange({ ...costs, [field]: value });
  };

  const isCMHC = ["cmhc", "cmhc_aph"].includes(financingType);

  if (!advancedExpenses) {
    const fields = [
      {
        field: "expertises",
        label: t("acquisitionCosts.expertises"),
        info: t("acquisitionCosts.expertisesInfo"),
      },
      { field: "notary", label: t("acquisitionCosts.notary"), info: t("acquisitionCosts.notaryInfo") },
      ...(isCMHC
        ? [
            { field: "cmhcAnalysis", label: t("acquisitionCosts.cmhcAnalysis"), info: t("acquisitionCosts.cmhcAnalysisInfo"), locked: true },
            { field: "cmhcTax", label: t("acquisitionCosts.cmhcTax"), info: t("acquisitionCosts.cmhcTaxInfo"), locked: true },
          ]
        : []),
      {
        field: "welcomeTax",
        label: t("acquisitionCosts.welcomeTax"),
        info: t("acquisitionCosts.welcomeTaxInfo"),
        locked: lockedFields?.welcomeTax,
      },
      {
        field: "otherFees",
        label: t("acquisitionCosts.otherFees"),
        info: t("acquisitionCosts.otherFeesInfo"),
      },
    ];

    return (
      <div className="border rounded-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold mb-4 text-orange-600 flex items-center">
          <Briefcase className="w-5 h-5 mr-2" />
          {t("acquisitionCosts.title")}
        </h2>
        <div className="grid grid-cols-1 gap-4">
          {fields.map(({ field, label, info, locked }) => (
            <div key={field}>
              <FieldHelpLabel help={info}>{label}</FieldHelpLabel>
              <FormattedNumberInput
                value={costs[field] || ""}
                onChange={(val) => handleChange(field, val)}
                className="w-full border rounded p-2"
                placeholder="0"
                disabled={locked}
                type="currency"
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <FieldHelpLabel help={t("acquisitionCosts.totalInfo")}>
            {t("acquisitionCosts.total")}
          </FieldHelpLabel>
          <FormattedNumberInput
            value={analysis.acquisitionCosts?.toString() || ''}
            onChange={() => {}}
            className="w-full border rounded p-2 bg-gray-50"
            placeholder="0"
            disabled
            type="currency"
          />
        </div>
      </div>
    );
  }

  const fields = [
    { field: "inspection", label: t("acquisitionCosts.inspection"), info: t("acquisitionCosts.inspectionInfo") },
    { field: "environmental1", label: t("acquisitionCosts.environmental1"), info: t("acquisitionCosts.environmental1Info") },
    { field: "environmental2", label: t("acquisitionCosts.environmental2"), info: t("acquisitionCosts.environmental2Info") },
    { field: "environmental3", label: t("acquisitionCosts.environmental3"), info: t("acquisitionCosts.environmental3Info") },
    { field: "appraiser", label: t("acquisitionCosts.appraiser"), info: t("acquisitionCosts.appraiserInfo") },
    ...(isCMHC
      ? [
          { field: "cmhcAnalysis", label: t("acquisitionCosts.cmhcAnalysis"), info: t("acquisitionCosts.cmhcAnalysisInfo"), locked: true },
          { field: "cmhcTax", label: t("acquisitionCosts.cmhcTax"), info: t("acquisitionCosts.cmhcTaxInfo"), locked: true },
        ]
      : []),
    { field: "otherFees", label: t("acquisitionCosts.otherFees"), info: t("acquisitionCosts.otherFeesInfo") },
    { field: "notary", label: t("acquisitionCosts.notary"), info: t("acquisitionCosts.notaryInfo") },
    { field: "welcomeTax", label: t("acquisitionCosts.welcomeTax"), info: t("acquisitionCosts.welcomeTaxInfo"), locked: lockedFields?.welcomeTax },
    { field: "renovations", label: t("acquisitionCosts.renovations"), info: t("acquisitionCosts.renovationsInfo") },
  ];

  return (
    <div className="border rounded-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold mb-4 text-orange-600 flex items-center">
          <Briefcase className="w-5 h-5 mr-2" />
          {t("acquisitionCosts.title")}
        </h2>
      <div className="grid grid-cols-1 gap-4">
        {fields.map(({ field, label, info, locked }) => (
          <div key={field}>
            <FieldHelpLabel help={info}>{label}</FieldHelpLabel>
            <FormattedNumberInput
              value={costs[field] || ""}
              onChange={(val) => handleChange(field, val)}
              className="w-full border rounded p-2"
              placeholder="0"
              disabled={locked}
              type="currency"
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <FieldHelpLabel help={t("acquisitionCosts.totalInfo")}>
          {t("acquisitionCosts.total")}
        </FieldHelpLabel>
        <FormattedNumberInput
          value={analysis.acquisitionCosts?.toString() || ''}
          onChange={() => {}}
          className="w-full border rounded p-2 bg-gray-50"
          placeholder="0"
          disabled
          type="currency"
        />
      </div>
    </div>
  );
}
